// Procedure fulfillments panel — Phase 2e (nurse execution UI, 2026-10-03).
//
// Rendered on EncounterDetailScreen below the service_requests section.
// Filters to service_type='procedure' and surfaces the nurse workflow:
//   - View fulfillment history for each procedure.
//   - Log a new fulfillment (nurse only).
//   - Close the order when done (nurse or doctor).
//   - Cancel the order (doctor only).
//
// This is deliberately a separate panel rather than inlined into the
// existing ServiceRequestsSection — the doctor's "write orders" surface
// stays untouched, and the nurse's "execute orders" surface has its own
// visual boundary.

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    ClinicMemberRole,
    ServiceRequest,
    ServiceRequestFulfillmentReq,
    ServiceRequestFulfillmentView,
} from '@pamosi/shared';
import { ApiError, clinicApi, proceduresApi } from '../lib/api';
import { uploadFulfillmentPhoto } from '../lib/photo-upload';
import { FulfillmentPhotoGallery } from './FulfillmentPhotoGallery';

interface Props {
    encounterId: string;
    serviceRequests: ServiceRequest[];
}

function canFulfill(role: ClinicMemberRole | null | undefined): boolean {
    return role === 'nurse' || role === 'admin';
}
function canClose(role: ClinicMemberRole | null | undefined): boolean {
    return role === 'nurse' || role === 'doctor' || role === 'provider' || role === 'admin';
}
function canCancel(role: ClinicMemberRole | null | undefined): boolean {
    return role === 'doctor' || role === 'provider' || role === 'admin';
}

export function ProcedureFulfillmentsPanel({ encounterId, serviceRequests }: Props) {
    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;

    const procedures = serviceRequests.filter((s) => s.service_type === 'procedure');
    if (procedures.length === 0) return null;

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Procedure fulfillment
                </div>
                <div className="text-slate-400 text-xs">{procedures.length} procedure{procedures.length === 1 ? '' : 's'}</div>
            </div>
            <ul className="space-y-3">
                {procedures.map((p) => (
                    <li key={p.id}>
                        <ProcedureRow
                            encounterId={encounterId}
                            request={p}
                            role={role}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

const STATUS_CLASS: Record<ServiceRequest['status'], string> = {
    requested: 'bg-sky-100 text-sky-800',
    in_progress: 'bg-amber-100 text-amber-800',
    fulfilled: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-slate-200 text-slate-600',
};

function ProcedureRow({
    encounterId,
    request,
    role,
}: {
    encounterId: string;
    request: ServiceRequest;
    role: ClinicMemberRole | null;
}) {
    const qc = useQueryClient();
    const closedOrCancelled = request.status === 'fulfilled' || request.status === 'cancelled';

    const historyQ = useQuery({
        queryKey: ['procedure-fulfillments', request.id],
        queryFn: () => proceduresApi.listFulfillments(request.id),
    });

    async function invalidateAll() {
        // The encounter detail owns service_requests, and the queue cache
        // feeds the Procedures tab. Also invalidate the fulfillment list
        // for this request so the row re-renders with the new history.
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['encounter', encounterId] }),
            qc.invalidateQueries({ queryKey: ['procedures-queue'] }),
            qc.invalidateQueries({ queryKey: ['procedure-fulfillments', request.id] }),
        ]);
    }

    return (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[request.status]}`}>
                            {request.status === 'in_progress' ? 'In progress' : request.status}
                        </span>
                        <span className="text-slate-900 text-sm font-medium">{request.description}</span>
                    </div>
                    {request.code && (
                        <div className="text-slate-500 text-xs mt-1">Code: {request.code}</div>
                    )}
                    {request.status === 'cancelled' && request.cancelled_reason && (
                        <div className="text-slate-500 text-xs mt-1 italic">
                            Cancelled: {request.cancelled_reason}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {canClose(role) && !closedOrCancelled && (
                        <CloseButton serviceRequestId={request.id} onDone={invalidateAll} />
                    )}
                    {canCancel(role) && !closedOrCancelled && (
                        <CancelButton serviceRequestId={request.id} onDone={invalidateAll} />
                    )}
                </div>
            </div>

            {/* Fulfillment history */}
            <div>
                <div className="text-slate-500 text-xs mb-1">Fulfillment history</div>
                {historyQ.isLoading && <div className="text-slate-400 text-xs">Loading…</div>}
                {historyQ.data?.fulfillments.length === 0 && (
                    <div className="text-slate-400 text-xs italic">No sessions logged yet.</div>
                )}
                {historyQ.data && historyQ.data.fulfillments.length > 0 && (
                    <ul className="divide-y divide-slate-100 border border-slate-100 rounded-md">
                        {historyQ.data.fulfillments.map((f) => (
                            <li key={f.id} className="p-2 text-sm">
                                <FulfillmentHistoryRow
                                    serviceRequestId={request.id}
                                    fulfillment={f}
                                    canAttach={canFulfill(role) && !closedOrCancelled}
                                    onPhotoAttached={invalidateAll}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {canFulfill(role) && !closedOrCancelled && (
                <FulfillmentForm
                    serviceRequestId={request.id}
                    onDone={invalidateAll}
                />
            )}
        </div>
    );
}

function FulfillmentHistoryRow({
    serviceRequestId,
    fulfillment: f,
    canAttach,
    onPhotoAttached,
}: {
    serviceRequestId: string;
    fulfillment: ServiceRequestFulfillmentView;
    canAttach: boolean;
    onPhotoAttached: () => void;
}) {
    return (
        <div className="min-w-0">
            <div className="text-slate-800">
                {f.fulfilled_by_full_name ?? 'Nurse'} ·{' '}
                <span className="text-slate-500 text-xs">
                    {new Date(f.performed_at).toLocaleString()}
                </span>
            </div>
            {(f.dose_text || f.batch_number || f.site) && (
                <div className="text-slate-500 text-xs mt-0.5 flex flex-wrap gap-x-3">
                    {f.dose_text && <span>Dose: {f.dose_text}</span>}
                    {f.site && <span>Site: {f.site}</span>}
                    {f.batch_number && <span>Batch: {f.batch_number}</span>}
                </div>
            )}
            {f.note && (
                <div className="text-slate-700 text-xs mt-1 whitespace-pre-wrap">{f.note}</div>
            )}
            <FulfillmentPhotoGallery
                serviceRequestId={serviceRequestId}
                fulfillmentId={f.id}
                photos={f.photos}
                canEdit={canAttach}
                onChanged={onPhotoAttached}
            />
        </div>
    );
}

function FulfillmentForm({
    serviceRequestId,
    onDone,
}: {
    serviceRequestId: string;
    onDone: () => void;
}) {
    const [note, setNote] = useState('');
    const [dose, setDose] = useState('');
    const [site, setSite] = useState('');
    const [batch, setBatch] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // Photo upload runs AFTER the fulfillment POST succeeds, so we track
    // its state separately. A photo failure doesn't roll back the already-
    // created fulfillment — the nurse retries via the row's "Add photo"
    // button.
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const mut = useMutation({
        mutationFn: (body: ServiceRequestFulfillmentReq) => proceduresApi.fulfill(serviceRequestId, body),
        onSuccess: async (res) => {
            setError(null);
            // If a photo was selected, run the upload pipeline before we
            // clear the form — a failure leaves the inputs in place so
            // the nurse can see which fulfillment it was for and retry.
            if (file) {
                setUploadingPhoto(true);
                setPhotoError(null);
                try {
                    await uploadFulfillmentPhoto({
                        serviceRequestId,
                        fulfillmentId: res.fulfillment.id,
                        file,
                    });
                } catch (err) {
                    setPhotoError(err instanceof ApiError
                        ? err.message
                        : err instanceof Error ? err.message : 'Photo upload failed.');
                    // Keep the form intact so the nurse sees the failure,
                    // but still refresh the history so the fulfillment
                    // itself shows up (they can hit "Add photo" there).
                    onDone();
                    setUploadingPhoto(false);
                    return;
                }
                setUploadingPhoto(false);
            }
            setNote(''); setDose(''); setSite(''); setBatch('');
            clearFile();
            onDone();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Could not log fulfillment.');
        },
    });

    function pickFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const picked = evt.target.files?.[0] ?? null;
        setPhotoError(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        if (!picked) {
            setFile(null);
            setPreviewUrl(null);
            return;
        }
        setFile(picked);
        setPreviewUrl(URL.createObjectURL(picked));
    }

    function clearFile() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function submit() {
        setError(null);
        if (note.trim() === '' && dose.trim() === '' && site.trim() === '' && batch.trim() === '' && !file) {
            setError('Enter at least a note, dose/site/batch, or attach a photo.');
            return;
        }
        mut.mutate({
            note: note.trim() === '' ? null : note.trim(),
            dose_text: dose.trim() === '' ? null : dose.trim(),
            site: site.trim() === '' ? null : site.trim(),
            batch_number: batch.trim() === '' ? null : batch.trim(),
        });
    }

    const busy = mut.isPending || uploadingPhoto;

    return (
        <div className="rounded-md border border-dashed border-slate-200 p-3 space-y-3 bg-slate-50">
            <div className="text-slate-700 text-sm font-medium">Log a session</div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="pm-label">Dose (optional)</label>
                    <input
                        className="pm-input"
                        type="text"
                        placeholder="e.g. 500 mg IM"
                        value={dose}
                        onChange={(e) => setDose(e.target.value)}
                        disabled={busy}
                        maxLength={200}
                    />
                </div>
                <div>
                    <label className="pm-label">Site (optional)</label>
                    <input
                        className="pm-input"
                        type="text"
                        placeholder="e.g. Left deltoid"
                        value={site}
                        onChange={(e) => setSite(e.target.value)}
                        disabled={busy}
                        maxLength={100}
                    />
                </div>
                <div className="col-span-2">
                    <label className="pm-label">Batch / Lot # (optional)</label>
                    <input
                        className="pm-input"
                        type="text"
                        placeholder="e.g. BX-23981"
                        value={batch}
                        onChange={(e) => setBatch(e.target.value)}
                        disabled={busy}
                        maxLength={100}
                    />
                </div>
                <div className="col-span-2">
                    <label className="pm-label">Note</label>
                    <textarea
                        className="pm-input min-h-20"
                        placeholder="e.g. Patient tolerated well, no adverse reaction."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={busy}
                        maxLength={2000}
                    />
                </div>
                <div className="col-span-2">
                    <label className="pm-label">Photo (optional)</label>
                    <div className="flex items-center gap-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={pickFile}
                            disabled={busy}
                        />
                        <button
                            type="button"
                            className="pm-btn-outline pm-btn-sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={busy}
                        >
                            {file ? 'Change photo' : 'Attach photo'}
                        </button>
                        {previewUrl && (
                            <div className="flex items-center gap-2">
                                <img
                                    src={previewUrl}
                                    alt="Selected photo"
                                    className="w-10 h-10 object-cover rounded border border-slate-200"
                                />
                                <button
                                    type="button"
                                    className="pm-btn-ghost pm-btn-sm text-slate-500"
                                    onClick={clearFile}
                                    disabled={busy}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                    {photoError && (
                        <div className="pm-error text-xs mt-1">
                            Session saved, but photo upload failed: {photoError}
                        </div>
                    )}
                </div>
            </div>
            {error && <div className="pm-error">{error}</div>}
            <div className="flex justify-end">
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={submit}
                    disabled={busy}
                >
                    {uploadingPhoto ? 'Uploading photo…' : mut.isPending ? 'Logging…' : 'Log session'}
                </button>
            </div>
        </div>
    );
}

function CloseButton({
    serviceRequestId,
    onDone,
}: {
    serviceRequestId: string;
    onDone: () => void;
}) {
    const [error, setError] = useState<string | null>(null);
    const mut = useMutation({
        mutationFn: () => proceduresApi.close(serviceRequestId),
        onSuccess: () => {
            setError(null);
            onDone();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Close failed.');
        },
    });
    return (
        <div className="flex flex-col items-end gap-1">
            <button
                type="button"
                className="pm-btn pm-btn-sm"
                onClick={() => mut.mutate()}
                disabled={mut.isPending}
            >
                {mut.isPending ? 'Closing…' : 'Mark fulfilled'}
            </button>
            {error && <div className="pm-error text-xs">{error}</div>}
        </div>
    );
}

function CancelButton({
    serviceRequestId,
    onDone,
}: {
    serviceRequestId: string;
    onDone: () => void;
}) {
    const [reason, setReason] = useState('');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mut = useMutation({
        mutationFn: () =>
            proceduresApi.cancel(serviceRequestId, {
                reason: reason.trim() === '' ? null : reason.trim(),
            }),
        onSuccess: () => {
            setError(null);
            setOpen(false);
            setReason('');
            onDone();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Cancel failed.');
        },
    });

    if (!open) {
        return (
            <button
                type="button"
                className="pm-btn-ghost pm-btn-sm text-rose-700"
                onClick={() => setOpen(true)}
            >
                Cancel order
            </button>
        );
    }
    return (
        <div className="flex flex-col items-end gap-1 min-w-56">
            <input
                className="pm-input"
                type="text"
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={mut.isPending}
                maxLength={500}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost pm-btn-sm"
                    onClick={() => { setOpen(false); setReason(''); }}
                    disabled={mut.isPending}
                >
                    Back
                </button>
                <button
                    type="button"
                    className="pm-btn pm-btn-sm text-rose-700"
                    onClick={() => mut.mutate()}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Cancelling…' : 'Confirm cancel'}
                </button>
            </div>
            {error && <div className="pm-error text-xs">{error}</div>}
        </div>
    );
}
